// @flow
import type { FSA } from 'types/redux';

import _ from 'lodash';
import { API_REQUEST } from 'middlewares/api-middleware';

import modelsConfig from 'data/models-config';
import Toastr from 'utils/toastr-util';

export function getCommonActionName(actionName: string, stateKey: string) {
  return `${actionName}_${_.toUpper(_.snakeCase(stateKey))}`;
}

export const customErrorHandler = (error: any, next: any, action: any, defaultErrorHandler: any) => {
  const errorDescription = _.get(error, 'response.data.message', '');
  if (_.isEmpty(errorDescription)) {
    defaultErrorHandler(error, next, action);
  } else {
    next(Toastr.error(
      errorDescription
    ));
  }
};

export const GET_ENTITIES = 'GET_ENTITIES';
export function getEntities(modelName: string, searchParams: Object): FSA {
  const { stateKey, apiPath, actions, isErrorHandler, useMock = false } = _.get(modelsConfig, modelName, {});
  if (_.isFunction(_.get(actions, 'list'))) return actions.list(modelName, searchParams);
  return {
    type: getCommonActionName(GET_ENTITIES, stateKey),
    payload: {
      method: 'GET',
      url: apiPath,
      params: searchParams,
      // Remove this later
      mock: useMock,
    },
    meta: {
      [API_REQUEST]: true,
      stateKey,
      errorHandler: isErrorHandler ? customErrorHandler : '',
    },
  };
}

export const GET_ENTITY = 'GET_ENTITY';
export function getEntity(modelName: string, entityID: number, params: Object = {}): FSA {
  const { stateKey, apiPath, actions, isErrorHandler, useMock = false } = _.get(modelsConfig, modelName, {});
  const { getPath = apiPath } = _.get(modelsConfig, modelName, {});
  if (_.isFunction(_.get(actions, 'fetch'))) return actions.fetch(modelName, entityID, params);
  return {
    type: getCommonActionName(GET_ENTITY, stateKey),
    payload: {
      method: 'GET',
      url: `${getPath}/${entityID}`,
      params,
      // Remove this later
      mock: useMock,
    },
    meta: {
      [API_REQUEST]: true,
      stateKey,
      errorHandler: isErrorHandler ? customErrorHandler : '',
    },
  };
}

export const CREATE_ENTITY = 'CREATE_ENTITY';
export function createEntity(modelName: string, entity: Object): FSA {
  const { stateKey, apiPath, isErrorHandler, actions, payloadKey: { create } = {}, viewBuilderParams: { payloadSendTransform } = {} } = _.get(modelsConfig, modelName, {});
  const { createPath = apiPath } = _.get(modelsConfig, modelName, '');
  const model = payloadSendTransform ? payloadSendTransform(entity) : entity;
  if (_.isFunction(_.get(actions, 'create'))) return actions.create(modelName, model);
  return {
    type: getCommonActionName(CREATE_ENTITY, stateKey),
    payload: {
      method: 'POST',
      url: createPath,
      data: create ? { [create]: model } : model,
    },
    meta: {
      [API_REQUEST]: true,
      stateKey,
      errorHandler: isErrorHandler ? customErrorHandler : '',
    },
  };
}

export const EDIT_ENTITY = 'EDIT_ENTITY';
export function editEntity(modelName: string, entityID: number, entity: Object): FSA {
  const { stateKey, apiPath, actions, isErrorHandler, editActionVerb: method = 'PUT', payloadKey: { edit } = {}, viewBuilderParams: { payloadSendTransform } = {} } = _.get(modelsConfig, modelName, {});
  const { editPath = apiPath } = _.get(modelsConfig, modelName, {});
  const model = payloadSendTransform ? payloadSendTransform(entity) : entity;
  if (_.isFunction(_.get(actions, 'edit'))) return actions.edit(modelName, entityID, model);
  return {
    type: getCommonActionName(EDIT_ENTITY, stateKey),
    payload: {
      method,
      url: `${editPath}/${entityID}`,
      data: edit ? { [edit]: model } : model,
    },
    meta: {
      [API_REQUEST]: true,
      stateKey,
      errorHandler: isErrorHandler ? customErrorHandler : '',
    },
  };
}

export const SET_ACTIVE_ENTITY = 'SET_ACTIVE_ENTITY';
export function setActiveEntity(modelName: string, entity: Object): FSA {
  const { stateKey, actions } = _.get(modelsConfig, modelName, {});
  if (_.isFunction(_.get(actions, 'setActive'))) return actions.setActive(modelName, entity);
  return {
    type: getCommonActionName(SET_ACTIVE_ENTITY, stateKey),
    payload: entity,
    meta: {
      stateKey,
    },
  };
}

export const DELETE_ENTITY = 'DELETE_ENTITY';
export function deleteEntity(modelName: string, entityID: number): FSA {
  const { stateKey, apiPath, actions, isErrorHandler } = _.get(modelsConfig, modelName, {});
  if (_.isFunction(_.get(actions, 'delete'))) return actions.delete(modelName, entityID);
  return {
    type: getCommonActionName(DELETE_ENTITY, stateKey),
    payload: {
      method: 'DELETE',
      url: `${apiPath}/${entityID}`,
    },
    meta: {
      [API_REQUEST]: true,
      stateKey,
      errorHandler: isErrorHandler ? customErrorHandler : '',
    },
  };
}

export const APPROVE_ENTITY = 'APPROVE_ENTITY';
export function approveEntity(modelName: string, entityID: number): FSA {
  const { stateKey, apiPath, actions } = _.get(modelsConfig, modelName, {});
  if (_.isFunction(_.get(actions, 'approve'))) return actions.approve(modelName, entityID);
  return {
    type: getCommonActionName(APPROVE_ENTITY, stateKey),
    payload: {
      method: 'POST',
      url: `${apiPath}/${entityID}/approve`,
    },
    meta: {
      [API_REQUEST]: true,
      stateKey,
    },
  };
}

export const ACTIVATE_ENTITY = 'ACTIVATE_ENTITY';
export function activateEntity(modelName: string, entityID: number, entity: Object, status: string): FSA {
  const { stateKey, apiPath, isErrorHandler, actions, viewBuilderParams: { payloadSendTransform } = {} } = _.get(modelsConfig, modelName, {});
  const { activatePath = apiPath } = _.get(modelsConfig, modelName, {});
  const model = payloadSendTransform ? payloadSendTransform(entity) : entity;
  if (_.isFunction(_.get(actions, 'activate'))) return actions.activate(modelName, entityID, model);
  const data = {
    status: status || model.status,
  };
  // Subscription plans/packages need to have version passed in
  if (modelName === 'subscriptionPlans' || modelName === 'subscriptionPackages') {
    data.version = entity.version;
  }
  return {
    type: getCommonActionName(ACTIVATE_ENTITY, stateKey),
    payload: {
      method: 'PUT',
      url: `${activatePath}/${entityID}/status`,
      data,
    },
    meta: {
      [API_REQUEST]: true,
      stateKey,
      errorHandler: isErrorHandler ? customErrorHandler : '',
    },
  };
}

export const DUPLICATE_ENTITY = 'DUPLICATE_ENTITY';
export function duplicateEntity(modelName: string, entityID: number, params: Object = {}): FSA {
  const { stateKey, apiPath, actions, isErrorHandler, useMock = false } = _.get(modelsConfig, modelName, {});
  const { getPath = apiPath } = _.get(modelsConfig, modelName, {});
  if (_.isFunction(_.get(actions, 'duplicate'))) return actions.duplicate(modelName, entityID, params);

  // Fallback for model config that overrides the default GET_ENTITY which was used to duplicate entity
  if (_.isFunction(_.get(actions, 'fetch'))) return actions.fetch(modelName, entityID, params);

  return {
    type: getCommonActionName(DUPLICATE_ENTITY, stateKey),
    payload: {
      method: 'GET',
      url: `${getPath}/${entityID}`,
      params,
      // Remove this later
      mock: useMock,
    },
    meta: {
      [API_REQUEST]: true,
      stateKey,
      errorHandler: isErrorHandler ? customErrorHandler : '',
    },
  };
}
